import { MapPin, Cake, Briefcase, HelpCircle } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

function compatBadgeClass(pct) {
  if (pct >= 70) return "mc-grad text-white";                              // alta: badge roxa cheia
  if (pct >= 40) return "bg-white text-violet-600 border border-violet-500"; // média: outline roxo
  return "bg-[#f0eef6] text-[#6b6580]";                                     // baixa: neutra, sem tom negativo
}

export function ProfileCard({ profile, onLike, liking }) {
  const pct = profile.compatibility_pct ?? 0;
  const standout = pct >= 80;
  return (
    <div className="mc-card overflow-hidden flex flex-col" data-testid={`profile-card-${profile.id}`}>
      <div className="relative">
        <img src={profile.photo_url} alt={profile.full_name} loading="lazy" className="w-full h-64 object-cover" />
        {standout && (
          <span data-testid={`badge-top-${profile.id}`}
            className="absolute top-3 left-3 mc-grad text-white text-xs font-semibold px-3 py-1 rounded-full shadow-md">
            Mais compatível
          </span>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="self-start flex items-center gap-1.5 mb-3">
          <span data-testid={`badge-${profile.id}`}
            className={`text-xs font-semibold px-3 py-1 rounded-full ${compatBadgeClass(pct)}`}>
            {pct}% compatível
          </span>
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" aria-label="Como calculamos esse percentual" data-testid={`badge-info-${profile.id}`}
                  className="text-muted-foreground hover:text-violet-600 transition-colors">
                  <HelpCircle size={14} />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-[220px] text-center">
                Calculado a partir das suas respostas de personalidade e hábitos no cadastro, mais os interesses em comum.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <h3 className="text-xl font-bold text-[#1e1b2e]">{profile.full_name}</h3>
        {profile.bio && <p className="text-sm text-muted-foreground mt-1">{profile.bio}</p>}
        <ul className="mt-3 space-y-1.5 text-sm text-[#3a3550]">
          {(profile.city || profile.state) && <li className="flex items-center gap-2"><MapPin size={15} className="text-violet-600" /> {[profile.city, profile.state].filter(Boolean).join(" – ")}</li>}
          {profile.age && <li className="flex items-center gap-2"><Cake size={15} className="text-violet-600" /> {profile.age} anos</li>}
          {profile.occupation && <li className="flex items-center gap-2"><Briefcase size={15} className="text-violet-600" /> {profile.occupation}</li>}
        </ul>
        <div className="flex flex-wrap gap-2 mt-4">
          {profile.tags?.map((t, i) => (
            <span key={i} className="text-xs bg-[#f4f2fb] text-[#4b2e83] px-3 py-1 rounded-full">{t.label} {t.icon}</span>
          ))}
        </div>
        <button className="btn-primary w-full mt-5" data-testid={`like-btn-${profile.id}`} disabled={liking || profile.liked} onClick={() => onLike(profile)}>
          {profile.liked ? "Curtido" : "Curtir"}
        </button>
      </div>
    </div>
  );
}

export function MatchCard({ profile, onMessage, onContinue }) {
  return (
    <div className="mc-card overflow-hidden mc-grad-premium p-6 flex flex-col items-center text-center" data-testid={`match-card-${profile.id}`}>
      <div className="relative h-40 w-full flex items-center justify-center mt-2">
        <img src={profile.photo_url} alt="match" className="w-24 h-32 object-cover rounded-xl rotate-[-8deg] absolute -translate-x-6 shadow-lg" />
        <img src="https://images.unsplash.com/photo-1530047139082-5435ca3c4614?crop=entropy&cs=srgb&fm=jpg&q=85&w=300" alt="você" className="w-24 h-32 object-cover rounded-xl rotate-[8deg] absolute translate-x-6 shadow-lg" />
      </div>
      <h3 className="text-2xl font-bold text-white mt-4">Deu Match!</h3>
      <p className="text-white/85 text-sm mt-1">Você e {profile.full_name} se curtiram.</p>
      <button className="w-full mt-5 rounded-full py-2.5 font-semibold bg-white text-violet-700 hover:opacity-90 transition" data-testid={`match-message-${profile.id}`} onClick={() => onMessage(profile)}>Enviar mensagem</button>
      <button className="w-full mt-3 rounded-full py-2.5 font-semibold bg-white/15 text-white hover:bg-white/25 transition" data-testid={`match-continue-${profile.id}`} onClick={onContinue}>Continuar vendo</button>
    </div>
  );
}
